import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Crown, RefreshCw, Printer, UserMinus, Check } from "lucide-react";
import { HouseholdControl } from "../components/cell/HouseholdControl";
import { AssignControl } from "../components/cell/AssignControl";
import { printGroupSheets } from "../components/cell/cellHelpers";
import { useAuth } from "../context/AuthContext";
import { CellTextButton } from "../components/CellTextButton";
import { CellCallButton, canCellCall } from "../components/CellCallButton";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const sm = "bg-[#EDF2F4] border border-[#EBE9E4] rounded-lg focus:ring-2 focus:ring-[#0B5E4E]/20 outline-none px-2.5 py-1.5 text-sm";
const HOST_STATUSES = ["Interested", "Contacted", "Confirmed", "Unavailable"];

export default function CellPlanning() {
  const { user } = useAuth();
  const canEdit = !!user?.caps?.cell_plan_edit;
  const canCall = canCellCall(user);
  const canGenerate = !!user?.caps?.cell_generate;
  const canApprove = !!user?.caps?.cell_approve;
  const [ws, setWs] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genUnassigned, setGenUnassigned] = useState([]);
  const [hostEdits, setHostEdits] = useState({});
  const [memberAvail, setMemberAvail] = useState({});
  const [dayByGroup, setDayByGroup] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [w, r] = await Promise.all([api.get("/cell/workspace"), api.get("/cell/responses")]);
      setWs(w.data); setResponses((r.data.responses || []).filter((x) => !x.excluded));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => ws?.plan || [], [ws]);
  const withPhone = useMemo(() => new Set(responses.filter((r) => r.phone).map((r) => r.record_id)), [responses]);

  const metrics = useMemo(() => {
    const assigned = new Set();
    groups.forEach((g) => (g.members || []).forEach((m) => assigned.add(m.recordId)));
    return {
      confirmedHosts: responses.filter((r) => r.host_status === "Confirmed").length,
      draftGroups: groups.filter((g) => g.status === "Draft").length,
      approvedGroups: groups.filter((g) => g.status === "Approved").length,
      unassigned: responses.filter((r) => !assigned.has(r.record_id)).length,
      availabilityMissing: responses.filter((r) => !r.availability).length,
      assigned,
    };
  }, [groups, responses]);

  const hostCandidates = useMemo(
    () => responses.filter((r) => r.willing_to_host || r.host_status).sort((a, b) => (a.postcode || "").localeCompare(b.postcode || "")),
    [responses]
  );

  const unassignedRows = useMemo(() => {
    const byId = Object.fromEntries(genUnassigned.map((u) => [u.record_id, u.reason]));
    return responses
      .filter((r) => !metrics.assigned.has(r.record_id))
      .map((r) => ({ ...r, reason: byId[r.record_id] || (!r.postcode_valid ? "Postcode requires review" : "Not in the current saved plan") }));
  }, [responses, metrics.assigned, genUnassigned]);

  const byId = useMemo(() => Object.fromEntries(responses.map((r) => [r.record_id, r])), [responses]);
  const approved = groups.filter((g) => g.status === "Approved");
  const print = (list) => { if (!printGroupSheets(list, byId)) toast.error("Allow pop-ups to print group sheets"); };

  const call = async (fn, msg) => {
    try { const { data } = await fn(); setWs(data); if (msg) toast.success(msg); return true; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };
  const saveHousehold = (m, val) => call(() => api.post("/cell/people", { record_id: m.recordId, household_id: val }), val ? `Household set for ${m.name}` : `Household cleared for ${m.name}`);
  const assign = async (row, groupId) => {
    const g = groups.find((x) => x.group_id === groupId);
    const ok = await call(() => api.post("/cell/plan/assign", { record_id: row.record_id, group_id: groupId }), `${row.name} added to ${g?.group_name || "group"}`);
    if (ok) setGenUnassigned(genUnassigned.filter((u) => u.record_id !== row.record_id));
    return ok;
  };
  const unassign = (g, m) => { if (window.confirm(`Remove ${m.name} from ${g.group_name}?`)) call(() => api.post("/cell/plan/unassign", { record_id: m.recordId, group_id: g.group_id }), `${m.name} removed`); };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/cell/plan/generate");
      setGenUnassigned(data.unassigned || []); setWs(data.workspace);
      toast.success(`Generated ${data.summary.groups} draft group(s)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setGenerating(false);
  };

  const saveHost = async (r, patch) => {
    const edit = { ...hostEdits[r.record_id], ...patch };
    try {
      await api.post("/cell/hosts", {
        record_id: r.record_id, name: r.name, postcode: r.postcode,
        status: edit.status ?? r.host_status ?? "Interested",
        capacity: Number(edit.capacity ?? r.host_capacity ?? 15),
        availability: edit.availability ?? r.availability ?? "",
      });
      toast.success(`${r.name} saved`);
      setHostEdits({ ...hostEdits, [r.record_id]: undefined });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveMemberAvailability = async (recordId, name) => {
    const val = memberAvail[recordId] || "";
    if (!val.trim()) { toast.error("Enter an availability first"); return; }
    try {
      await api.post("/cell/people", { record_id: recordId, availability: val });
      toast.success(`Availability saved for ${name}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const approveGroup = async (g) => {
    const day = (dayByGroup[g.group_id] || g.meeting_day || "").trim();
    if (!day) { toast.error("Enter a meeting day first"); return; }
    try {
      const { data } = await api.post("/cell/plan/approve", { group_id: g.group_id, meeting_day: day });
      setWs(data); toast.success(`${g.group_name} approved`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="cell-planning-page">
      <div className="sj-page-hero flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-semibold" style={{ color: "var(--sj-text)" }}>Cell Group — Group planning</h1>
          <p className="text-sm mt-1 max-w-2xl" style={{ color: "var(--sj-text-2)" }}>The Data Lead generates draft groups. The pastor approves each group once a host is confirmed, availability and a meeting day are recorded. Groups hold no more than 15 people, including the host, and keep exact postcodes together where capacity allows.</p>
        </div>
        <button data-testid="cell-refresh" onClick={load} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
          <RefreshCw className="h-4 w-4" /> Refresh planning
        </button>
      </div>

      {/* Workflow metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="cell-workflow-metrics">
        {[["Confirmed hosts", metrics.confirmedHosts, "Available group locations"],
          ["Draft groups", metrics.draftGroups, "Awaiting approval"],
          ["Approved groups", metrics.approvedGroups, "Approved by pastor"],
          ["Unassigned", metrics.unassigned, "Require review or capacity"],
          ["Availability missing", metrics.availabilityMissing, "Needed before approval"]].map(([l, n, sub]) => (
          <div key={l} className={`${CARD} p-4`} data-testid={`cell-metric-${l.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-2xl font-semibold" style={{ color: "var(--sj-text)" }}>{n}</div>
            <div className="text-xs mt-0.5 font-medium" style={{ color: "var(--sj-text-2)" }}>{l}</div>
            <div className="text-[11px]" style={{ color: "var(--sj-text-3)" }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Host confirmation */}
      <div className={`${CARD} overflow-hidden`} data-testid="cell-host-confirmation">
        <div className="px-5 pt-4 pb-2">
          <h3 className="font-head text-lg font-semibold">Host confirmation</h3>
          <p className="text-xs" style={{ color: "var(--sj-text-3)" }}>Only respondents marked <b>Confirmed</b> can anchor a generated cell group. Capacity includes the host.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider" style={{ color: "var(--sj-text-3)", background: "#F7F7F5" }}>
                <th className="px-5 py-2.5">Potential host</th><th className="px-5 py-2.5">Postcode</th><th className="px-5 py-2.5">Status</th><th className="px-5 py-2.5">Capacity</th><th className="px-5 py-2.5">Availability</th>{canEdit && <th className="px-5 py-2.5"></th>}
              </tr>
            </thead>
            <tbody>
              {hostCandidates.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-6 text-center" style={{ color: "var(--sj-text-3)" }}>No potential hosts yet. Mark willing respondents on the Responses page.</td></tr>
              ) : hostCandidates.map((r) => {
                const e = hostEdits[r.record_id] || {};
                return (
                  <tr key={r.record_id} data-testid={`cell-hostrow-${r.record_id}`} className="border-t" style={{ borderColor: "var(--sj-border)" }}>
                    <td className="px-5 py-2.5 font-medium">{r.name}</td>
                    <td className="px-5 py-2.5">{r.postcode || "—"}</td>
                    <td className="px-5 py-2.5">
                      {canEdit ? (
                        <select data-testid={`cell-hoststatus-${r.record_id}`} value={e.status ?? r.host_status ?? "Interested"} onChange={(ev) => setHostEdits({ ...hostEdits, [r.record_id]: { ...e, status: ev.target.value } })} className={sm}>
                          {HOST_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: r.host_status === "Confirmed" ? "#E3F1EC" : "#FBEEE6", color: r.host_status === "Confirmed" ? "#0B5E4E" : "#B45309" }}>{r.host_status || "Interested"}</span>}
                    </td>
                    <td className="px-5 py-2.5">
                      {canEdit ? <input data-testid={`cell-hostcap-${r.record_id}`} type="number" min={1} max={15} value={e.capacity ?? r.host_capacity ?? 15} onChange={(ev) => setHostEdits({ ...hostEdits, [r.record_id]: { ...e, capacity: ev.target.value } })} className={sm + " w-16"} /> : (r.host_capacity || "—")}
                    </td>
                    <td className="px-5 py-2.5">
                      {canEdit ? <input data-testid={`cell-hostavail-${r.record_id}`} value={e.availability ?? r.availability ?? ""} onChange={(ev) => setHostEdits({ ...hostEdits, [r.record_id]: { ...e, availability: ev.target.value } })} placeholder="e.g. Tuesday evenings" className={sm + " w-40"} /> : (r.availability || <span style={{ color: "#D97706" }}>Not set</span>)}
                    </td>
                    {canEdit && <td className="px-5 py-2.5 text-right"><button data-testid={`cell-hostsave-${r.record_id}`} onClick={() => saveHost(r, e)} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs text-white" style={{ background: "var(--sj-primary)" }}><Check className="h-3.5 w-3.5" /> Save</button></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Generated groups */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="font-head text-lg font-semibold">Generated groups</h3>
            <p className="text-xs" style={{ color: "var(--sj-text-3)" }}>Exact postcodes are kept together where capacity allows. Max 15 people per group, including the host.</p>
          </div>
          <div className="flex flex-wrap gap-2">
          {approved.length > 0 && (
            <button data-testid="cell-print-all" onClick={() => print(approved)} className="inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
              <Printer className="h-4 w-4" /> Print approved sheets ({approved.length})
            </button>
          )}
          {canGenerate && (
            <button data-testid="cell-generate-button" disabled={generating} onClick={generate} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50" style={{ background: "#F2C14E", color: "#4a3a10" }}>
              <Sparkles className="h-4 w-4" /> {generating ? "Generating…" : "Generate draft groups"}
            </button>
          )}
          </div>
        </div>

        {loading ? (
          <div className={`${CARD} p-8 text-center`} style={{ color: "var(--sj-text-3)" }}>Loading…</div>
        ) : groups.length === 0 ? (
          <div className={`${CARD} p-8 text-center`} style={{ color: "var(--sj-text-3)" }}>No groups yet. {canGenerate ? "Confirm at least one host above, then generate." : "A Data Lead needs to generate draft groups."}</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="cell-groups">
            {groups.map((g) => {
              const members = g.members || [];
              const ready = members.every((m) => m.availability) && members.length <= g.capacity;
              const flags = (g.flags || []).filter((f) => !(f === "Availability incomplete" && members.every((m) => m.availability)));
              const hhCount = members.reduce((a, m) => { const k = (m.householdId || "").toLowerCase(); if (k) a[k] = (a[k] || 0) + 1; return a; }, {});
              return (
                <div key={g.group_id} data-testid={`cell-group-${g.group_id}`} className={`${CARD} p-5`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-head text-lg font-semibold">{g.group_name}</h4>
                      <p className="text-xs" style={{ color: "var(--sj-text-3)" }}>Host: {g.host_name} · {members.length}/{g.capacity} people{g.meeting_day ? ` · ${g.meeting_day}` : ""}</p>
                    </div>
                    <span className="flex items-center gap-2 shrink-0">
                      {g.status === "Approved" && <button data-testid={`cell-print-${g.group_id}`} onClick={() => print([g])} title="Print group sheet" className="rounded-full border p-1.5" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><Printer className="h-3.5 w-3.5" /></button>}
                      <span data-testid={`cell-group-status-${g.group_id}`} className="text-xs font-semibold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: g.status === "Approved" ? "#E3F1EC" : "#FBEEE6", color: g.status === "Approved" ? "#0B5E4E" : "#B45309" }}>{g.status === "Approved" && <CheckCircle2 className="h-3.5 w-3.5" />}{g.status}</span>
                    </span>
                  </div>
                  {flags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {flags.map((f, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "#FBE9D7", color: "#8a5a1a" }}>{f}</span>)}
                    </div>
                  )}
                  <div className="mt-3 space-y-1.5 max-h-60 overflow-y-auto pr-1">
                    {members.map((m) => (
                      <div key={m.recordId} data-testid={`cell-member-${g.group_id}-${m.recordId}`} className="flex items-center justify-between gap-2 text-sm">
                        <span className="flex items-center gap-1.5 min-w-0" style={{ color: "var(--sj-text)" }}>
                          {m.role === "Host" && <Crown className="h-3.5 w-3.5 shrink-0" style={{ color: "#D97706" }} />}
                          <span className="truncate">{m.name}</span>
                          <span className="text-xs shrink-0" style={{ color: "var(--sj-text-3)" }}>{m.postcode}</span>
                          <HouseholdControl key={m.householdId || ""} member={m} canEdit={canEdit} isFamily={(hhCount[(m.householdId || "").toLowerCase()] || 0) > 1} onSave={(v) => saveHousehold(m, v)} />
                        </span>
                        <span className="flex items-center gap-1 shrink-0">
                        {canCall && withPhone.has(m.recordId) && <><CellCallButton recordId={m.recordId} name={m.name} label="" /><CellTextButton recordId={m.recordId} name={m.name} label="" /></>}
                        {m.availability ? (
                          <span className="text-xs shrink-0" style={{ color: "var(--sj-text-3)" }}>{m.availability}</span>
                        ) : canEdit ? (
                          <span className="flex items-center gap-1 shrink-0">
                            <input data-testid={`cell-memavail-${m.recordId}`} value={memberAvail[m.recordId] || ""} onChange={(ev) => setMemberAvail({ ...memberAvail, [m.recordId]: ev.target.value })} placeholder="set availability" className={sm + " w-28 py-1"} />
                            <button data-testid={`cell-memavail-save-${m.recordId}`} onClick={() => saveMemberAvailability(m.recordId, m.name)} className="rounded px-2 py-1 text-xs text-white" style={{ background: "var(--sj-primary)" }}>Save</button>
                          </span>
                        ) : <span className="text-xs shrink-0" style={{ color: "#D97706" }}>no availability</span>}
                        {canEdit && m.role !== "Host" && <button data-testid={`cell-unassign-${g.group_id}-${m.recordId}`} onClick={() => unassign(g, m)} title="Remove from group" className="rounded p-1 opacity-40 hover:opacity-100" style={{ color: "#B42318" }}><UserMinus className="h-3.5 w-3.5" /></button>}
                        </span>
                      </div>
                    ))}
                  </div>
                  {g.status === "Draft" && (
                    <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-3" style={{ borderColor: "var(--sj-border)" }}>
                      <input data-testid={`cell-group-day-${g.group_id}`} value={dayByGroup[g.group_id] ?? g.meeting_day ?? ""} onChange={(e) => setDayByGroup({ ...dayByGroup, [g.group_id]: e.target.value })} placeholder="Meeting day e.g. Tuesday 7pm" className={sm + " flex-1 min-w-[160px]"} disabled={!canApprove} />
                      {canApprove && (
                        <button data-testid={`cell-approve-${g.group_id}`} disabled={!ready} onClick={() => approveGroup(g)} title={ready ? "" : "Every member needs availability and the group must be within capacity"} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white disabled:opacity-40 shrink-0" style={{ background: "var(--sj-primary)" }}>
                          <CheckCircle2 className="h-4 w-4" /> Approve group
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Unassigned responses */}
      <div className={`${CARD} overflow-hidden`} data-testid="cell-unassigned">
        <div className="px-5 pt-4 pb-2">
          <h3 className="font-head text-lg font-semibold">Unassigned responses</h3>
          <p className="text-xs" style={{ color: "var(--sj-text-3)" }}>These remain visible until a valid postcode or suitable host capacity is available.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider" style={{ color: "var(--sj-text-3)", background: "#F7F7F5" }}>
                <th className="px-5 py-2.5">Name</th><th className="px-5 py-2.5">Postcode</th><th className="px-5 py-2.5">Reason</th>{canEdit && groups.length > 0 && <th className="px-5 py-2.5">Assign to group</th>}
              </tr>
            </thead>
            <tbody>
              {unassignedRows.length === 0 ? (
                <tr><td colSpan={4} className="px-5 py-6 text-center" style={{ color: "var(--sj-text-3)" }}>Everyone has been placed into a group.</td></tr>
              ) : unassignedRows.map((r) => (
                <tr key={r.record_id} data-testid={`cell-unassigned-${r.record_id}`} className="border-t" style={{ borderColor: "var(--sj-border)" }}>
                  <td className="px-5 py-2.5 font-medium">{r.name}</td>
                  <td className="px-5 py-2.5">{r.postcode || <span style={{ color: "#D97706" }}>missing</span>}</td>
                  <td className="px-5 py-2.5" style={{ color: "var(--sj-text-3)" }}>{r.reason}</td>
                  {canEdit && groups.length > 0 && <td className="px-5 py-2.5"><AssignControl row={r} groups={groups} onAssign={assign} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
